import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import {
  Dialog,
  DialogAction,
  DialogActions,
  DialogContent,
} from "components/Dialog";
import RadioInput from "components/Inputs/RadioInput";
import {
  ApiError,
  MeetingAdminResponse,
  MeetingLimitedResponse,
  MeetingsService,
  SessionFrequency,
} from "client/openapi";
import { CHANGE_OPTIONS } from "components/MeetingDialog/Popups/changeOptions";
import Notifications from "util/notifications";
import { announceMeetingsChanged } from "util/meetingsChanged";
import moment from "moment";
import { useState } from "react";
import { PageStatus } from "types";
import { CalendarIcon } from "@radix-ui/react-icons";

const TIME_FORMAT = "h:mma";

export default function RescheduleConfirmDialog({
  meeting,
  newStart,
  newDuration,
  onConfirm,
  onCancel,
}: {
  meeting: MeetingAdminResponse | MeetingLimitedResponse;
  newStart: Date;
  newDuration: number;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [status, setStatus] = useState<PageStatus>();
  const isRecurring = meeting.session.frequency !== SessionFrequency.SINGLE;
  const [changeType, setChangeType] = useState<CHANGE_OPTIONS>(
    CHANGE_OPTIONS.JUST_THIS_SESSION
  );

  const oldStart = moment(meeting.start);
  const oldEnd = oldStart.clone().add(meeting.duration, "minutes");
  const newStartMoment = moment(newStart);
  const newEnd = newStartMoment.clone().add(newDuration, "minutes");

  const handleUpdateApplyChanges = (
    ev: React.ChangeEvent<HTMLInputElement>
  ) => {
    setChangeType(ev.target.value as CHANGE_OPTIONS);
  };

  async function handleConfirm() {
    setStatus(PageStatus.LOADING);
    try {
      await MeetingsService.editMeeting({
        meetingId: meeting.id,
        requestBody: {
          start: moment.utc(newStart).format(),
          duration: newDuration,
          apply_to_all_future_meetings:
            changeType === CHANGE_OPTIONS.ALL_FUTURE_OCCURRENCES,
        },
      });
      announceMeetingsChanged();
      Notifications.success("Meeting rescheduled!");
      onConfirm();
    } catch (error: unknown) {
      if (error instanceof ApiError) {
        setStatus(PageStatus.ERROR);
        Notifications.error("Error: " + error.message + ". Please try again.");
        console.error(`Error (#${error.status}): ${error.message}`);
      } else {
        setStatus(PageStatus.ERROR);
        Notifications.error("An unexpected error occurred. Please try again.");
        console.error(`Unexpected error: ${error}`);
      }
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent
        className="dialog-content dialog-content--left wide h-fit max-h-[85vh] lg:max-w-[33vw] md:w-1/2 w-3/4 bg-neutral-50 font-montserrat pb-4 overflow-scroll"
        alignLeft={true}
        showClose={false}
      >
        <div className="meeting-dialog-new--title pt-4">
          <span>Reschedule Meeting?</span>
        </div>

        <div className="flex flex-col mt-2">
          <span className="font-semibold text-sm mb-1">Old Time</span>
          <span className="flex items-center text-sm mb-3">
            <CalendarIcon height="20" width="20" />
            &nbsp;{oldStart.format("ddd, MMM Do")} from{" "}
            {oldStart.format(TIME_FORMAT)} to {oldEnd.format(TIME_FORMAT)}
          </span>
          <span className="font-semibold text-sm mb-1">New Time</span>
          <span className="flex items-center text-sm mb-3">
            <CalendarIcon height="20" width="20" />
            &nbsp;{newStartMoment.format("ddd, MMM Do")} from{" "}
            {newStartMoment.format(TIME_FORMAT)} to {newEnd.format(TIME_FORMAT)}
          </span>
        </div>

        {isRecurring && (
          <div className="mt-4">
            <div className="font-bold mb-2 text-md">Apply changes to:</div>
            <div className="flex flex-row items-center gap-2 mb-2">
              <RadioInput
                id={CHANGE_OPTIONS.JUST_THIS_SESSION}
                name="reschedule-scope"
                value={CHANGE_OPTIONS.JUST_THIS_SESSION}
                label="Just this meeting"
                setValue={handleUpdateApplyChanges}
                checked={changeType === CHANGE_OPTIONS.JUST_THIS_SESSION}
                required
              />
            </div>
            <div className="flex flex-row items-center gap-2 mb-2">
              <RadioInput
                id={CHANGE_OPTIONS.ALL_FUTURE_OCCURRENCES}
                name="reschedule-scope"
                value={CHANGE_OPTIONS.ALL_FUTURE_OCCURRENCES}
                label={`All future occurrences, switching to ${newStartMoment.format(
                  "dddd"
                )}s from ${newStartMoment.format(
                  TIME_FORMAT
                )} to ${newEnd.format(TIME_FORMAT)}`}
                setValue={handleUpdateApplyChanges}
                checked={changeType === CHANGE_OPTIONS.ALL_FUTURE_OCCURRENCES}
                required
              />
            </div>
          </div>
        )}

        <DialogActions>
          <DialogAction
            size={ButtonSize.SMALL}
            color={
              status === PageStatus.LOADING
                ? ButtonColor.GRAY
                : ButtonColor.GREEN
            }
            fill={ButtonFill.DEFAULT}
            disabled={status === PageStatus.LOADING}
            onClick={handleConfirm}
          >
            {status === PageStatus.LOADING ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
            ) : (
              <>Confirm</>
            )}
          </DialogAction>
          <Button
            size={ButtonSize.SMALL}
            color={ButtonColor.BLACK}
            fill={ButtonFill.HOLLOW}
            onClick={onCancel}
          >
            Cancel
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
}
